import type { IRequiredAcceptanceResponse } from '@escloud/types';
import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useNavigate } from 'react-router';
import { toast } from 'sonner';

import { LEGAL_DOCUMENTS } from './documents.config';
import { LegalProse } from './legal-prose';
import { resolveLocale } from './locale';
import { useLegalContent } from './use-legal-content';

import { ChangeEmailDialog } from '@/components/account/change-email-dialog';
import { RetryPanel } from '@/components/feedback/retry-panel';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { useTenant } from '@/context';
import { DIALOG_CLAIMS_POLL_MS, useClaimsRefreshPoll } from '@/hooks/use-claims-refresh-poll';
import { useCurrentUser, useOnboardingState } from '@/hooks/use-current-user';
import { useEmailVerificationState } from '@/hooks/use-email-verification-state';
import { useRecordAcceptance, useRequiredAcceptances } from '@/hooks/use-legal';
import { useVerificationResend } from '@/hooks/use-verification-resend';
import { errorIds, logError } from '@/lib/log-error';

/**
 * The device-use gate (ESCLOUD-287) — the innermost tenant-scoped step of the
 * router gating chain, which is declared once in `GATING_CHAIN`
 * (`router/index.tsx`).
 *
 * Mounted as a route guard over the whole
 * `/tenants/:tenantId` subtree and the viewer — every device page, the case list
 * and the trash included, since both carry patient identifiers the server
 * refuses to a caller who has not attested. What stays outside it is
 * administration: tenant settings and the user's own profile, so a buyer can
 * still invite colleagues and manage billing.
 *
 * Four conditions gate the clinical route: a verified email, a complete
 * professional profile, the qualification attestation and the IFU
 * acknowledgement. This dialog no longer collects the profile itself - that
 * moved to `/welcome/profile` (`components/profile`) as part of the funnel
 * (ESCLOUD-333) - it only points there while the profile is incomplete, and
 * re-triggers by itself when either document's version is bumped (the gate
 * query compares against current versions).
 *
 * Unlike the EULA gate this deliberately fails CLOSED on a query error: the
 * server refuses the clinical endpoints with `403 attestation_required` anyway
 * (the enforcement point of the qualification risk control), so failing open
 * here would only render clinical screens whose every request is refused.
 * Email verification is a soft gate by design (ESCLOUD-333): no route guard
 * blocks an unverified identity, so settings, billing and the member list stay
 * reachable. The server enforces it at exactly two points, the clinical tier
 * (this guard's server twin refuses every case, data, job and workflow
 * endpoint with 403 attestation_required while the claim is unverified) and
 * invitation sending (403 email_verification_required). This dialog is the
 * client side of the first; the account banner asks for verification earlier,
 * without blocking.
 *
 * Whether the profile counts as complete is NOT decided here. It comes from
 * `GET /users/me/onboarding-state`, which recomputes it from the server's
 * current required-field list — the same list the guard enforcing the 403
 * recomputes from. The user row is still fetched, for the email shown in the
 * verification panel only.
 */
export function DeviceUseGate() {
    const userQuery = useCurrentUser();
    const onboardingQuery = useOnboardingState();
    const pendingQuery = useRequiredAcceptances({ scope: 'device_use' });

    if (userQuery.isPending || onboardingQuery.isPending || pendingQuery.isPending) {
        return (
            <div className="flex h-full items-center justify-center py-24">
                <Spinner />
            </div>
        );
    }

    if (userQuery.isError || onboardingQuery.isError || pendingQuery.isError) {
        return (
            <RetryPanel
                title="Qualification status could not be verified"
                description="Clinical functions require a confirmed professional qualification, and it could not be checked right now. Retry, or contact contact@therenva.com if this persists."
                onRetry={() => {
                    void userQuery.refetch();
                    void onboardingQuery.refetch();
                    void pendingQuery.refetch();
                }}
            />
        );
    }

    const { emailVerified, profileComplete } = onboardingQuery.data;
    const pendingDocuments = pendingQuery.data;

    if (emailVerified && profileComplete && pendingDocuments.length === 0) {
        return <Outlet />;
    }

    return (
        <DeviceUseDialog
            email={userQuery.data.email}
            emailVerified={emailVerified}
            profileComplete={profileComplete}
            pendingDocuments={pendingDocuments}
            onEmailVerifiedHint={() => void onboardingQuery.refetch()}
        />
    );
}

interface IDeviceUseDialogProps {
    email: string;
    /** The server's verdict, never re-derived here. */
    emailVerified: boolean;
    profileComplete: boolean;
    pendingDocuments: IRequiredAcceptanceResponse[];
    /** Called after a resend turns out to mean the address was already verified. */
    onEmailVerifiedHint: () => void;
}

function DeviceUseDialog({
    email,
    emailVerified,
    profileComplete,
    pendingDocuments,
    onEmailVerifiedHint,
}: IDeviceUseDialogProps) {
    const navigate = useNavigate();
    const { tenantId } = useTenant();
    const { resend, isPending: resendPending } = useVerificationResend({
        email,
        onAlreadyVerified: onEmailVerifiedHint,
    });
    const recordAcceptance = useRecordAcceptance();

    const pendingAttestation = pendingDocuments.find(
        (doc) => doc.documentType === 'professional_attestation'
    );
    const pendingIfu = pendingDocuments.find((doc) => doc.documentType === 'ifu');

    const [attestationConfirmed, setAttestationConfirmed] = useState(false);
    const [ifuAcknowledged, setIfuAcknowledged] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [changing, setChanging] = useState(false);

    const attestationConfig = LEGAL_DOCUMENTS.professionalAttestation;
    const attestationLocale = resolveLocale(attestationConfig.locales);
    const attestationContent = useLegalContent('professional-attestation', attestationLocale);

    // The attestation checkbox only counts once its statement actually rendered —
    // an acceptance of a document that never displayed is not valid evidence.
    const attestationReady =
        !pendingAttestation || (attestationConfirmed && attestationContent.data !== undefined);
    const canSubmit =
        emailVerified &&
        profileComplete &&
        attestationReady &&
        (!pendingIfu || ifuAcknowledged) &&
        !submitting;

    const submit = async () => {
        setSubmitting(true);
        try {
            // Order matters for evidence quality: the attestation is recorded
            // before the IFU acknowledgement that follows it. Every step is
            // idempotent server-side, so a failure partway is safely retried by
            // pressing the button again.
            if (pendingAttestation) {
                await recordAcceptance.mutateAsync({
                    documentType: 'professional_attestation',
                    documentVersion: pendingAttestation.documentVersion,
                });
            }
            if (pendingIfu) {
                await recordAcceptance.mutateAsync({
                    documentType: 'ifu',
                    documentVersion: pendingIfu.documentVersion,
                });
            }
            // No navigation: the mutations updated the gate queries, so the guard
            // re-evaluates to complete and renders the clinical route.
        } catch (error) {
            logError(errorIds.DEVICE_USE_GATE_SUBMIT_FAILED, 'Device-use gate submission failed', {
                error,
            });
            toast.error('Your confirmation could not be recorded', {
                description: 'Nothing was lost — please try again.',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const cancel = () => {
        // Administration is what an un-attested user still reaches, so that is
        // where declining leads. Not the workspace root: every device page sits
        // behind this gate, so `/tenants/:tenantId` would re-display the dialog
        // — and `/` redirects there. Navigating back to the cases later DOES
        // re-display it, by design; landing on it from Cancel would be a trap.
        void navigate(tenantId ? `/settings/tenant/${tenantId}/general` : '/profile', { replace: true });
    };

    return (
        <>
            {!emailVerified && <EmailVerificationWatcher onVerified={onEmailVerifiedHint} />}
            <Dialog open>
                {/* Blocking for this route: no onOpenChange, no close button - the ways out
                are completing the step or returning to the workspace. */}
                <DialogContent
                    className="flex max-h-[90vh] flex-col sm:max-w-2xl [&>button]:hidden"
                    onPointerDownOutside={(event) => event.preventDefault()}
                    onEscapeKeyDown={(event) => event.preventDefault()}
                    onInteractOutside={(event) => event.preventDefault()}
                >
                    <DialogHeader>
                        <DialogTitle>Before your first clinical use</DialogTitle>
                        <DialogDescription>
                            ESCloud is intended for qualified healthcare professionals. Complete your
                            professional profile and confirm your qualification to create cases and open
                            workflows.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="min-h-0 flex-1 space-y-6 overflow-y-auto pr-1">
                        {!emailVerified && (
                            <section className="space-y-3">
                                <h3 className="text-sm font-semibold">Email verification</h3>
                                <p className="text-sm text-muted-foreground">
                                    Verify your email address first. We sent a link to {email}. Safety notices
                                    about this device are sent to this address, so it must reach you.
                                </p>
                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => void resend()}
                                        disabled={resendPending}
                                    >
                                        {resendPending ? 'Requesting…' : 'Resend verification email'}
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => setChanging(true)}>
                                        Change address
                                    </Button>
                                </div>
                            </section>
                        )}

                        {!profileComplete && (
                            <section className="space-y-2">
                                <h3 className="text-sm font-semibold">Professional profile</h3>
                                <p className="text-sm text-muted-foreground">
                                    Your professional profile is incomplete.{' '}
                                    <Link
                                        to="/welcome/profile"
                                        className="font-medium underline underline-offset-2"
                                    >
                                        Complete your profile
                                    </Link>{' '}
                                    and come back here.
                                </p>
                            </section>
                        )}

                        {pendingAttestation && (
                            <section className="space-y-3">
                                <h3 className="text-sm font-semibold">Qualification</h3>
                                <div className="max-h-48 overflow-y-auto rounded-md border p-3.5">
                                    {attestationContent.isPending && (
                                        <div className="flex justify-center py-4">
                                            <Spinner />
                                        </div>
                                    )}
                                    {attestationContent.isError && (
                                        <p className="py-4 text-center text-sm text-destructive">
                                            The attestation statement could not be loaded, so it cannot be
                                            confirmed. Please reload the application.
                                        </p>
                                    )}
                                    {attestationContent.data !== undefined && (
                                        <LegalProse html={attestationContent.data} />
                                    )}
                                </div>
                                <div className="flex items-start gap-3">
                                    <Checkbox
                                        id="dug-attestation"
                                        checked={attestationConfirmed}
                                        onCheckedChange={(checked) =>
                                            setAttestationConfirmed(checked === true)
                                        }
                                        className="mt-0.5"
                                    />
                                    <label htmlFor="dug-attestation" className="text-sm leading-normal">
                                        I confirm the statement above (version{' '}
                                        {pendingAttestation.documentVersion})
                                    </label>
                                </div>
                            </section>
                        )}

                        {pendingIfu && (
                            <section className="space-y-3">
                                <h3 className="text-sm font-semibold">Instructions for use</h3>
                                <div className="flex items-start gap-3">
                                    <Checkbox
                                        id="dug-ifu"
                                        checked={ifuAcknowledged}
                                        onCheckedChange={(checked) => setIfuAcknowledged(checked === true)}
                                        className="mt-0.5"
                                    />
                                    <label htmlFor="dug-ifu" className="text-sm leading-normal">
                                        I have read the{' '}
                                        <Link
                                            to="/eifu"
                                            target="_blank"
                                            className="font-medium underline underline-offset-2"
                                        >
                                            Instructions for Use
                                        </Link>{' '}
                                        (version {pendingIfu.documentVersion}, software{' '}
                                        {LEGAL_DOCUMENTS.eifu.softwareVersion})
                                    </label>
                                </div>
                            </section>
                        )}
                    </div>

                    <div className="flex items-center justify-end gap-2 border-t pt-4">
                        <Button variant="outline" onClick={cancel} disabled={submitting}>
                            Not now
                        </Button>
                        <Button onClick={() => void submit()} disabled={!canSubmit}>
                            {submitting ? 'Recording…' : 'Confirm and continue'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <ChangeEmailDialog open={changing} onOpenChange={setChanging} currentEmail={email} />
        </>
    );
}

/**
 * Notices a verification that happens while the dialog is open — the link
 * clicked in the inbox, often in another tab.
 *
 * The server reads `emailVerified` from the access token, and the token minted
 * at sign-in cannot know about that click, so refetching the onboarding state
 * alone would keep answering "unverified". This polls a forced token refresh
 * (every `DIALOG_CLAIMS_POLL_MS`, and on returning to the tab) and, once the
 * fresh claim reads verified, asks the server again with that token.
 *
 * Mounted on the server's answer, not the claim's: a refresh drops the claim
 * to `pending` while in flight, and unmounting on that would restart the poll
 * (whose first tick is immediate) after every refresh.
 */
function EmailVerificationWatcher({ onVerified }: { onVerified: () => void }) {
    useClaimsRefreshPoll(DIALOG_CLAIMS_POLL_MS);
    const state = useEmailVerificationState();

    // The callback is re-created on every render of the gate; only a change of
    // verdict may trigger a refetch.
    const onVerifiedRef = useRef(onVerified);
    onVerifiedRef.current = onVerified;

    useEffect(() => {
        if (state === 'verified') onVerifiedRef.current();
    }, [state]);

    return null;
}
